import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import axios from "axios";
import { useReactToPrint } from 'react-to-print';
import { toast } from "react-toastify";
import { getLoggedInUser, getRefreshToken } from '../../../services/AuthService';
import { UserLogout } from '../../layouts/nav/Logout';
import COMMONVARIABLES from '../Utilities/constants';
import BILLING_API from '../../Api/BillingApi';
import COMMON_API from '../../Api/CommonApi';
import AmountInWords from '../Utilities/AmountInWords';
import Logo from "../../../pictures/bw1.png";

const POSBillPrint = forwardRef((props, ref) => {
    const BILLING_BASE_URL = process.env.REACT_APP_BASE_URL;
    const BASE_URL = process.env.REACT_APP_BASE_URL;

    const contentToPrint = useRef(null);
    const [printData, setPrintData] = useState(null);
    const [printTitle, setPrintTitle] = useState('');
    const [printCustomer, setPrintCustomer] = useState(null);
    const [printCustomerText, setPrintCustomerText] = useState('');
    const [printCustomerAddress, setPrintCustomerAddress] = useState(null);
    const [printCustomerCity, setPrintCustomerCity] = useState(null);
    const [printCustomerGstin, setPrintCustomerGstin] = useState(null);
    const [localUserDetails, setLocalUserDetails] = useState(null);
    const [localBranchDetails, setLocalBranchDetails] = useState(null);
    const [isPrint, setIsPrint] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const branchDetails = localStorage.getItem("branchDetails");
        const userDetails = localStorage.getItem("userDetails");
        if (branchDetails) setLocalBranchDetails(JSON.parse(branchDetails));
        if (userDetails) setLocalUserDetails(JSON.parse(userDetails));
    }, []);

    const handlePrint = useReactToPrint({
        documentTitle: "Print This Document",
        onBeforePrint: () => {
            let temp = document.querySelectorAll(".printfooter");
            for (let i = 0; i < temp.length; i++) {
                if ((i + 1) == temp.length)
                    temp[i].style.display = "block";
            }
        },
        onAfterPrint: () => {
            setIsPrint(0);
            setPrintData(null);
        },
        removeAfterPrint: true,
    });

    useEffect(() => {
        if (isPrint == 1 && printData) {
            handlePrint(null, () => contentToPrint.current);
            setIsPrint(0);
        }
    }, [printData, isPrint]);

    const getCustomerDetails = async (customerCode) => {
        try {
            const cached = localStorage.getItem('customerDetails');
            if (cached) {
                const list = JSON.parse(cached);
                const found = list.find(e => e.cm_customer_code === customerCode);
                if (found) return found;
            }

            const user = getLoggedInUser();
            const config = { headers: { authorization: 'Bearer '.concat(user.accessToken) } };
            const customerParams = { user_id: Number(user?.id), status_code: Number(0) };
            const customerData = await axios.post(BASE_URL + COMMON_API.CUSTOMER_MASTER_LIST, customerParams, config);

            if (customerData?.data?.body?.status === COMMONVARIABLES.STATUS.UNAUTHORIZED) {
                const refresh = await getRefreshToken();
                if (refresh) {
                    if (Number(refresh) === COMMONVARIABLES.STATUS.EXPIRED_TOKEN) {
                        UserLogout();
                    } else {
                        return await getCustomerDetails(customerCode);
                    }
                }
                return null;
            }

            const list = customerData?.data?.body?.customerDetails || customerData?.data?.body || [];
            return Array.isArray(list) ? list.find(e => e.cm_customer_code === customerCode) : null;
        } catch (error) {
            console.error('Error fetching customer details:', error);
            return null;
        }
    };

    const bindPrintLoadData = async (billing) => {
        const customerDetails = await getCustomerDetails(billing.ps_customer_id);
        setPrintCustomer({ label: customerDetails?.cm_customer_name || billing.ps_customer_text, value: customerDetails?.cm_customer_code ?? billing.ps_customer_id });
        setPrintCustomerAddress(customerDetails?.cm_address || '');
        setPrintCustomerCity(customerDetails?.cm_city || '');
        setPrintCustomerGstin(customerDetails?.cm_gstin || '');
        setPrintCustomerText(billing.ps_customer_text);
        setPrintTitle(billing.vt_print_title);
    };

    const loadAndPrint = async (transactionNo, branchCode) => {
        try {
            if (!transactionNo) {
                toast.error("Invalid bill details", { position: toast.POSITION.TOP_RIGHT });
                return;
            }
            setIsLoading(true);
            const user = getLoggedInUser();
            const editloadParams = {
                branch_trans_no: transactionNo,
                branch_code: branchCode || localBranchDetails?.bm_branch_code || Number(localStorage.getItem("branch_code")),
                user_id: user?.id
            };
            const config = { headers: { authorization: 'Bearer '.concat(user.accessToken) } };
            const editloadValues = await axios.post(BILLING_BASE_URL + BILLING_API.BILLING_EDIT_LOAD, editloadParams, config);

            if (editloadValues?.data?.body?.status === COMMONVARIABLES.STATUS.UNAUTHORIZED) {
                const refresh = await getRefreshToken();
                if (refresh) {
                    if (Number(refresh) === COMMONVARIABLES.STATUS.EXPIRED_TOKEN) {
                        UserLogout();
                    } else {
                        await loadAndPrint(transactionNo, branchCode);
                    }
                }
                setIsLoading(false);
                return;
            }

            if (editloadValues?.data?.body?.billingList) {
                await bindPrintLoadData(editloadValues.data.body.billingList[0]);
                setIsPrint(1);
                setPrintData(editloadValues.data.body);
            } else {
                toast.error(editloadValues?.data?.body?.message || "Unable to load bill for print", { position: toast.POSITION.TOP_RIGHT });
            }
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error('Error loading bill for print:', error);
            toast.error("Unable to load bill for print", { position: toast.POSITION.TOP_RIGHT });
        }
    };

    useImperativeHandle(ref, () => ({
        print: loadAndPrint,
        isLoading
    }));

    return (
        <div style={{ visibility: 'hidden', height: 0, overflow: 'hidden' }}>
            <div ref={contentToPrint} className="row contentToPrint" style={{ padding: '16px' }}>
                <table>
                    <style>
                        {`
       @page {    
        size: A5 portrait;    
        padding-left: 8px;    
        padding-right: 0px;
    }  

    @media print {
  .print-footer {
    text-align: center;
    font-size: 16px;
    padding-top:5px;
    background: white;
  }
}
        `}
                    </style>

                    <thead><tr><td>
                        <div className="printheader">
                            <div className="col-12">
                                <div className="row">
                                    <div className="col-6">
                                        <div className="brand-logo p-16">
                                            <img className="logo-abbr me-3" src={Logo} alt="" style={{ width: '170px', height: '100px' }} />
                                        </div>
                                    </div>
                                    <div className='col-6 text-end'>
                                        <span>
                                            <strong style={{ fontSize: 20, fontWeight: 700 }} className="d-block text-black"> {localUserDetails?.company_name}</strong>
                                        </span>
                                        <small className="text-black" style={{ fontSize: 16, lineHeight: 1.2 }}>
                                            {localBranchDetails?.bm_address}{', '}{localBranchDetails?.bm_city}
                                            <br></br>
                                            {localUserDetails?.state_name}
                                            <br></br>
                                            {'Email'}{' : '}{localUserDetails?.email_id}
                                            <br></br>
                                            {'Contact No'}{' : '}{localUserDetails?.mobile_no}
                                            <strong className="d-block">GSTIN : {localUserDetails?.gstin}</strong>
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <div className="h_line"></div>
                            <strong className="d-block text-center" style={{ textTransform: 'uppercase' }}> {printTitle}</strong>
                            <div className="h_line"></div>
                        </div>
                    </td></tr></thead>
                    <tbody>
                        <tr>
                            <td>
                                <div className='row'>
                                    {printCustomer?.value == 0 ?
                                        <div className=" col-6">
                                            <h6>To</h6>
                                            <strong>{printCustomerText} {'-'}{printData?.billingList[0]?.ps_customer_mobileno}</strong>
                                        </div> :
                                        <div className="col-6">
                                            <h6>To</h6>
                                            <div>
                                                {" "}
                                                <strong>{printCustomer?.label}</strong>{" "}
                                            </div>
                                            <div>{printCustomerAddress}</div>
                                            <div>{printCustomerCity}</div>
                                            {printCustomerGstin != '' ? <div>GSTIN : {printCustomerGstin}</div> : null}
                                        </div>}

                                    <div className="text-end col-6">
                                        <span className="d-block">Invoice No. : <strong>{printData?.billingList[0]?.ps_bill_text}</strong></span>
                                        <span className="d-block">Invoice Date : <strong>{printData?.billingList[0]?.ps_billing_date}</strong></span>
                                    </div>
                                </div>
                                <div className="h_line"></div>
                            </td>
                        </tr>
                        <tr><td>
                            <div className="col-lg-12 ">
                                <div className="table-responsive">
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th className="center">#</th>
                                                <th style={{ textAlign: 'center !important' }} className="center">Item</th>
                                                <th>HSN</th>
                                                <th className="text-end">Rate</th>
                                                <th className="center">Qty</th>
                                                <th className="text-end">Disc</th>
                                                <th className="text-end">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                printData?.billingItemList?.length > 0 ?
                                                    printData?.billingItemList?.map((row, index) => {
                                                        return (
                                                            <tr key={index}>
                                                                <td>{index + 1}</td>
                                                                <td className='text-black'> <span className=" text-black  d-block">  {row.itemname}</span></td>
                                                                <td>    {row?.im_hsn_code != "" ? <div className="fs-10 d-block">{row?.im_hsn_code}</div> : "-"}
                                                                </td>
                                                                <td className='text-end text-black'>{COMMONVARIABLES.India_Rupee_Formatter_Digits(row.rate)}
                                                                    {row?.im_tax_per != "" ? <div>{'('}{row?.im_tax_per}{'%)'}</div> : null}
                                                                </td>
                                                                <td className='center text-black'>{row.itemqty}</td>
                                                                <td className='text-end text-black'>{COMMONVARIABLES.India_Rupee_Formatter_Digits(row.discamt)}</td>
                                                                <td className='text-end text-black'>{COMMONVARIABLES.India_Rupee_Formatter_Digits(row.amount)}</td>
                                                            </tr>
                                                        )
                                                    })
                                                    : ''
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </td></tr></tbody>
                    <tfoot><tr><td>
                        <div className="footer-space">      <div className="printfooter" >
                            <div className="h_line"></div>
                            <div className='row' style={{ marginTop: '50px' }}>
                                <div className=" col-8">
                                    <small className="text-black">Total Items : {printData?.billingItemList?.length}</small> {'  '}
                                    <small className="text-black">Total Qty : {printData?.billingItemList?.reduce(function (sum, i) {
                                        return sum + i.itemqty;
                                    }, 0)}</small>

                                    {printCustomerGstin?.substring(0, 2) === "33" || printCustomerGstin?.substring(0, 2) === "" || printCustomerGstin?.substring(0, 2) == undefined ?
                                        <div> <small className="text-black">CGST(Rs.) : {COMMONVARIABLES.India_Rupee_Formatter_Digits((printData?.billingList[0]?.ps_tax_amount / 2))} {' '}SGST(Rs.) : {COMMONVARIABLES.India_Rupee_Formatter_Digits((printData?.billingList[0]?.ps_tax_amount / 2))}</small></div>
                                        :
                                        <small className="text-black">IGST(Rs.) : {COMMONVARIABLES.India_Rupee_Formatter_Digits(printData?.billingList[0]?.ps_tax_amount)}</small>}
                                    <div className='text-black'>Amount in Words :    <AmountInWords amount={printData?.billingList[0]?.ps_total_amount} /></div>
                                    <div className="h_line"></div>

                                    <span>
                                        <strong className=" text-black  d-block">    {"Bank Details"}</strong>
                                    </span>
                                    <small className="text-black">
                                        Bank Name : {localUserDetails?.bank_name}
                                        <br></br>
                                        IFSC Code : {localUserDetails?.ifsc_code}
                                        <br></br>
                                        Account Number : {localUserDetails?.account_number}
                                        <br></br>
                                        Account Holder Name : {localUserDetails?.cm_account_name}
                                    </small>
                                </div>
                                <div className="col-4">
                                    <table className="table table-clear">
                                        <tbody>
                                            <tr>
                                                <td className="left">
                                                    <strong>Subtotal</strong>
                                                </td>
                                                <td className="text-end text-black" style={{ paddingRight: "50px" }}>{COMMONVARIABLES.India_Rupee_Formatter_Digits(printData?.billingList[0]?.ps_amount)}</td>
                                                <td style={{ width: "20px" }}>&nbsp;&nbsp;&nbsp;</td>
                                                <td style={{ width: "20px" }}></td>
                                            </tr>
                                            <tr>
                                                <td className="left">
                                                    <strong>Discount</strong>
                                                </td>
                                                <td className="text-end text-black" style={{ paddingRight: "50px" }}>{COMMONVARIABLES.India_Rupee_Formatter_Digits(printData?.billingList[0]?.ps_discount)}</td>
                                                <td style={{ width: "20px" }}>&nbsp;&nbsp;&nbsp;</td>
                                                <td style={{ width: "20px" }}></td>
                                            </tr>
                                            {printData?.billingList[0]?.ps_buy_back_amount > 0 ?
                                                <tr>
                                                    <td className="left">
                                                        <strong>Buyback({COMMONVARIABLES.India_Rupee_Formatter_Digits(printData?.billingList[0]?.ps_buy_back_kg)})</strong>
                                                    </td>
                                                    <td className="text-end text-black" style={{ paddingRight: "50px" }}>{COMMONVARIABLES.India_Rupee_Formatter_Digits(printData?.billingList[0]?.ps_buy_back_amount)}</td>
                                                    <td style={{ width: "20px" }}>&nbsp;&nbsp;&nbsp;</td>
                                                    <td style={{ width: "20px" }}></td>
                                                </tr>
                                                : null}
                                            <tr>
                                                <td className="left">
                                                    <strong className='fs-18'>Total</strong>
                                                </td>
                                                <td className="text-end fs-18 text-black font-w700" style={{ paddingRight: "50px" }}>
                                                    {COMMONVARIABLES.India_Rupee_Formatter_Digits(printData?.billingList[0]?.ps_total_amount)}
                                                </td>
                                                <td style={{ width: "20px" }}>&nbsp;&nbsp;&nbsp;</td>
                                                <td style={{ width: "20px" }}></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div className="h_line"></div>
                                </div>
                            </div>
                            <div className="h_line"></div>
                            <div className="print-footer">
                                <strong>No Exchange No Return</strong>
                            </div>
                            <div className='' style={{ marginTop: '50px' }}>
                                <div className='row'>
                                    <div className='col-6'>
                                        <strong>Customer's Sign</strong>
                                    </div>
                                    <div className='col-6 text-end'>
                                        <strong>Authorised Signatory</strong>
                                    </div>
                                </div>
                            </div>
                        </div> </div>
                    </td></tr></tfoot>
                </table>
            </div>
        </div>
    );
});

export default POSBillPrint;
